import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { createPageUrl } from 'utils';
import { motion } from 'framer-motion';
import { Button } from 'Components/ui/button';
import { Badge } from 'Components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from 'Components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'Components/ui/dialog';
import ContactForm from 'Components/shared/ContactForm';
import {
    Shield,
    Clock,
    CheckCircle,
    Phone,
    ArrowRight,
    Wrench,
    Zap,
    AlertTriangle,
    FileText,
    Calendar
} from 'lucide-react';

const plans = [
    {
        name: 'Base',
        id: 'base',
        price: 'Su richiesta',
        description: 'Ideale per piccole aziende con 1-2 UPS',
        features: [
            '1 visita di manutenzione annuale',
            'Controllo e pulizia generale',
            'Test batterie',
            'Report tecnico',
            'Supporto telefonico',
        ],
        highlighted: false
    },
    {
        name: 'Professional',
        id: 'professional',
        price: 'Su richiesta',
        description: 'Per aziende con esigenze di continuità',
        features: [
            '2 visite di manutenzione annuali',
            'Controllo e pulizia approfondita',
            'Test batterie e sostituzione preventiva',
            'Analisi termica componenti',
            'Report tecnico dettagliato',
            'Supporto telefonico prioritario',
            'Intervento entro 24h in caso di guasto',
            'Sconto 15% sui ricambi',
        ],
        highlighted: true
    },
    {
        name: 'Enterprise',
        id: 'enterprise',
        price: 'Su richiesta',
        description: 'Per infrastrutture critiche',
        features: [
            '4 visite di manutenzione annuali',
            'Manutenzione completa certificata',
            'Sostituzione preventiva batterie inclusa',
            'Monitoraggio remoto 24/7',
            'Report tecnici trimestrali',
            'Supporto dedicato h24',
            'Intervento urgente entro 4h',
            'Sconto 25% sui ricambi',
            'UPS sostitutivo in caso di guasto prolungato',
        ],
        highlighted: false
    },
];

const benefits = [
    {
        icon: Shield,
        title: 'Protezione Continuativa',
        description: 'Mantieni i tuoi UPS sempre efficienti e pronti a proteggere la tua attività'
    },
    {
        icon: Clock,
        title: 'Riduzione Fermi Macchina',
        description: 'La manutenzione preventiva riduce drasticamente i guasti improvvisi'
    },
    {
        icon: Zap,
        title: 'Massima Efficienza',
        description: 'Batterie sempre al massimo delle prestazioni, consumi ottimizzati'
    },
    {
        icon: AlertTriangle,
        title: 'Intervento Prioritario',
        description: 'In caso di emergenza, i clienti con contratto hanno la priorità assoluta'
    },
];

export default function Manutenzione() {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [submitted, setSubmitted] = useState(false);

    const handleSelectPlan = (plan) => {
        setSelectedPlan(plan);
        setDialogOpen(true);
    };

    return (
        <div className="min-h-screen bg-white">
            {/* Hero */}
            <section className="relative bg-gray-900 pt-32 pb-20">
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1625314887424-9f190599bd56?w=1920&q=80"
                        alt="UPS Systems"
                        className="w-full h-full object-cover opacity-20"
                    />
                </div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <Badge className="bg-emerald-500 text-white mb-6">Contratti di Manutenzione</Badge>
                        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                            Manutenzione Programmata UPS
                        </h1>
                        <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                            Previeni i guasti e assicura la massima efficienza dei tuoi gruppi di continuità
                            con i nostri piani di manutenzione personalizzati.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Benefits */}
            <section className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">Perché un contratto di manutenzione?</h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Un UPS non mantenuto può guastarsi proprio quando ne hai più bisogno
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {benefits.map((benefit, index) => (
                            <motion.div
                                key={benefit.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <Card className="h-full text-center hover:shadow-lg transition-shadow">
                                    <CardContent className="pt-8">
                                        <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-blue-100 flex items-center justify-center">
                                            <benefit.icon className="w-7 h-7 text-blue-600" />
                                        </div>
                                        <h3 className="font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                                        <p className="text-sm text-gray-600">{benefit.description}</p>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Plans */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">Scegli il tuo piano</h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Piani flessibili per ogni esigenza, con copertura personalizzata in base al numero e alla tipologia di UPS
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {plans.map((plan, index) => (
                            <motion.div
                                key={plan.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <Card className={`h-full relative overflow-hidden ${plan.highlighted
                                    ? 'border-2 border-blue-600 shadow-xl'
                                    : 'border-gray-200'
                                    }`}>
                                    {plan.highlighted && (
                                        <div className="absolute top-0 left-0 right-0 bg-blue-600 text-white text-center py-1 text-sm font-medium">
                                            Più Richiesto
                                        </div>
                                    )}
                                    <CardHeader className={plan.highlighted ? 'pt-10' : ''}>
                                        <CardTitle className="text-center">
                                            <span className="text-sm font-medium text-blue-600 uppercase tracking-wider">
                                                {plan.name}
                                            </span>
                                            <div className="text-3xl font-bold text-gray-900 mt-2">{plan.price}</div>
                                            <p className="text-sm text-gray-500 font-normal mt-2">{plan.description}</p>
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        <ul className="space-y-3">
                                            {plan.features.map((feature) => (
                                                <li key={feature} className="flex items-start gap-2">
                                                    <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                                                    <span className="text-sm text-gray-600">{feature}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        <Button
                                            onClick={() => handleSelectPlan(plan)}
                                            className={`w-full rounded-full ${plan.highlighted
                                                ? 'bg-blue-600 hover:bg-blue-700'
                                                : 'bg-gray-900 hover:bg-gray-800'
                                                } !text-white`}
                                        >
                                            Richiedi Preventivo
                                        </Button>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Included Services */}
            <section className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                        >
                            <h2 className="text-3xl font-bold text-gray-900 mb-6">
                                Cosa include ogni visita di manutenzione
                            </h2>
                            <div className="space-y-4">
                                {[
                                    { icon: Wrench, text: 'Ispezione visiva e pulizia componenti' },
                                    { icon: Zap, text: 'Test di funzionamento e simulazione blackout' },
                                    { icon: FileText, text: 'Verifica parametri e aggiornamento firmware' },
                                    { icon: AlertTriangle, text: 'Controllo stato batterie e autonomia residua' },
                                    { icon: Calendar, text: 'Pianificazione sostituzione preventiva componenti' },
                                    { icon: FileText, text: 'Report tecnico dettagliato' },
                                ].map((item) => (
                                    <div key={item.text} className="flex items-center gap-4 p-4 bg-white rounded-xl">
                                        <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                                            <item.icon className="w-5 h-5 text-blue-600" />
                                        </div>
                                        <span className="text-gray-700">{item.text}</span>
                                    </div>
                                ))}
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="relative"
                        >
                            <img
                                src="https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=800&q=80"
                                alt="Tecnico al lavoro"
                                className="rounded-3xl shadow-2xl"
                            />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 bg-blue-600">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl font-bold text-white mb-4">
                        Hai domande sui nostri piani?
                    </h2>
                    <p className="text-xl text-blue-100 mb-8">
                        Contattaci per una consulenza gratuita e un preventivo personalizzato
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a href="tel:+393296139684">
                            <Button size="lg" className="w-full sm:w-auto rounded-full bg-white text-blue-600 hover:bg-blue-50 gap-2">
                                <Phone className="w-5 h-5" />
                                329 6139684
                            </Button>
                        </a>
                        <Link to={createPageUrl('Contatti')}>
                            <Button size="lg" variant="outline" className="w-full sm:w-auto rounded-full border-white text-white hover:bg-white/10 gap-2">
                                Contattaci
                                <ArrowRight className="w-5 h-5" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Quote Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="sm:max-w-lg bg-white">
                    <DialogHeader>
                        <DialogTitle>
                            {submitted ? 'Richiesta Inviata!' : `Richiedi Informazioni - Piano ${selectedPlan?.name}`}
                        </DialogTitle>
                    </DialogHeader>

                    {submitted ? (
                        <div className="text-center py-8">
                            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold mb-2">Grazie per la tua richiesta!</h3>
                            <p className="text-gray-600 mb-6">
                                Ti contatteremo al più presto con un preventivo personalizzato.
                            </p>
                            <Button
                                onClick={() => {
                                    setDialogOpen(false);
                                    setSubmitted(false);
                                }}
                                className="rounded-full"
                            >
                                Chiudi
                            </Button>
                        </div>
                    ) : (
                        <ContactForm
                            defaultType="manutenzione"
                            prefilledMessage={`Piano selezionato: ${selectedPlan?.name}`}
                            showTitle={false}
                            onSuccess={() => setSubmitted(true)}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}